import React, { useState } from "react";
import InstallationModel from "../../core/models/Installation";
import {
  Theme,
  makeStyles,
  createStyles,
  withStyles,
  Tooltip,
  Typography,
  IconButton,
  AccordionSummary,
  Accordion,
  AccordionDetails,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
  Grid,
  Container,
  Card,
  Button,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { IEnumerable } from "../../core/models/ModelHelpers";
import { isArrayValid } from "../../helpers/utility";
import { useHubx, useHubXSubscribe } from "../../HubX";
import { EVENT_HOME_PAGE_SEARCH_CLICKED } from "../../Events";
import useConstructor from "../../core/hooks/useConstructor";
import { DEFAULT_MAX_ITEM_PER_PAGE } from "../../core/constants/appGlobals";
import { Workflow } from "../../core/models/Enum/Workflow";
import IPagination from "../../core/contracts/IPagination";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { DELETE_GROUP, FETCH_BUSINESS_EVENTS, FETCH_GROUPS_LIST } from "../../Fedx";
import BusinessEvent from "../../core/models/BusinessEvent";
import OrderingType from "../../core/models/OrderingType";
import IFilter from "../../core/contracts/IFilter";
import { Link, useHistory } from "react-router-dom";
import {
  BUSINESS_EVENT_STATUS_MAP,
  WAITING_APPROVAL_EMISSION,
  WAITING_CONFIRMATION_NO_EMISSION,
} from "../../core/models/EventStatus";
import CreateIcon from "@material-ui/icons/Create";
import DeleteIcon from "@material-ui/icons/Delete";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import EGroup from "../../core/contracts/EGroup";
import { useSnackbar } from "notistack";
import SimpleDialog from "../SimpleDialog";
import SimpleTable from "../SimpleTable";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableHeader: {
      "& .MuiTableCell-stickyHeader": { backgroundColor: "#1D212A" },
      "& .MuiTableCell-head": { color: "#FFFFFF" },
    },

    card: {
      padding: theme.spacing(2),
    },

    button: {
      display: "flex",
      alignItems: "center",
      columnGap: `${theme.spacing(1)}px`,
    },

    deleteButton: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.common.white,
    },

    dialogContainer: {
      padding: theme.spacing(2),
    },
  })
);

const headerArray = ["Grupo", "Descrição", "N° de Membros", "Permissões", "Ações"];

function ListingTable() {
  const hub = useHubx();
  const classes = useStyles();
  const toastr = useSnackbar();
  const history = useHistory();

  const [groups, setGroups] = React.useState<null | EGroup[]>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [openDialogDelete, setOpenDialogDelete] = React.useState<boolean>(false);
  const [loadingDelete, setLoadingDelete] = React.useState<boolean>(false);
  const [deleteGroup, setDeleteGroup] = React.useState<string>("");

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const groupsList: EGroup[] = await hub.request(FETCH_GROUPS_LIST);
      if (isArrayValid(groupsList)) {
        setGroups(groupsList);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      toastr.enqueueSnackbar("Ops! Ocorreu um problema ao obter os grupos!", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useConstructor(() => {
    fetchGroups();
  });

  const renderValue = (value?: string | null | number) => {
    return value || value === 0 ? value : "-";
  };

  const getPermissions = (group: EGroup) => {
    const permissions = [];

    if (group.aprovacao_nivel1) permissions.push("Aprovação para emissão");

    if (group.aprovacao_nivel2) permissions.push("Aprovação de não emissão 2 nível");

    if (group.atualizacao_cadastral) permissions.push("Atualização cadastral");

    if (group.novo_pedido_fluxo6) permissions.push("Entrar com novo pedido (fluxo 6)");

    if (group.novo_pedido_fluxo7_9) permissions.push("Entrar com novo pedido (fluxo 7)");

    if (group.ver_pedidos) permissions.push("Ver Pedidos");

    if (isArrayValid(permissions)) {
      return permissions.join(", ");
    }

    return "-";
  };

  const handleClickDelete = async () => {
    setOpenDialogDelete(false);
    setLoadingDelete(true);
    try {
      const APIResponse = await hub.request(DELETE_GROUP, deleteGroup);
      if (APIResponse) {
        toastr.enqueueSnackbar("Grupo excluído com sucesso!", { variant: "success" });
        fetchGroups();
      } else {
        toastr.enqueueSnackbar(
          "Ops! Não foi possível exluir esse grupo! Nenhuma alteração foi feita!",
          { variant: "warning" }
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      toastr.enqueueSnackbar("Ops! Ocorreu um erro ao tentar excluir o grupo!", {
        variant: "error",
      });
    } finally {
      setLoadingDelete(false);
      setDeleteGroup("");
    }
  };

  const handleCloseDialogDelete = () => {
    setDeleteGroup("");
    setOpenDialogDelete(false);
  };

  const tableRows = React.useMemo(() => {
    return (
      groups?.map((group) => {
        const showLoading = loadingDelete && deleteGroup === group.id;
        const result = [
          renderValue(group.nome),
          renderValue(group.descricao),
          renderValue(group?.usuarios?.length),
          getPermissions(group),
          <>
            {showLoading ? (
              <CircularProgress />
            ) : (
              <>
                <Tooltip title="Editar grupo">
                  <IconButton onClick={() => history.push(`/group/${group.id}`)} size="small">
                    <CreateIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Deletar grupo">
                  <span>
                    <IconButton
                      onClick={() => {
                        setOpenDialogDelete(true);
                        setDeleteGroup(group?.id ?? "");
                      }}
                      size="small"
                      disabled={group?.tem_restricao_cliente || group?.atualizacao_cadastral}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </>,
        ];

        return result;
      }) ?? []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  return (
    <>
      <Container maxWidth="xl">
        <Card className={classes.card}>
          <Typography component="h1" variant="h5" color="textSecondary">
            Grupos
          </Typography>
          <Grid container justify="flex-end">
            <Button
              onClick={() => history.push("/group/new/")}
              variant="text"
              className={classes.button}
            >
              <AddCircleOutlineIcon fontSize="large" />
              <span>Novo Grupo</span>
            </Button>
          </Grid>

          <SimpleTable loading={loading} header={headerArray} rows={tableRows} />
        </Card>
      </Container>

      <SimpleDialog
        title="Confirmar exclusão"
        open={openDialogDelete}
        handleClose={handleCloseDialogDelete}
      >
        <div className={classes.dialogContainer}>
          <DialogContentText>
            <div>Deseja realmente excluir esse grupo?</div>
            <strong>Essa ação é irreversível!</strong>
          </DialogContentText>

          <DialogActions>
            <Button onClick={handleCloseDialogDelete} variant="outlined">
              Cancelar
            </Button>

            <Button
              onClick={handleClickDelete}
              variant="contained"
              className={classes.deleteButton}
            >
              Excluir
            </Button>
          </DialogActions>
        </div>
      </SimpleDialog>
    </>
  );
}
export default ListingTable;
